import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  Button,
  CircularProgress,
} from "@mui/material";
import TableCompo from "../CommonTable/TableCompo";
import { useNavigate } from "react-router-dom";
import { getDepositPlansRequest } from "../../redux/DepositePlan/depositeSlice";
import { useDispatch, useSelector } from "react-redux";
import { useAppTheme } from "../../theme/useAppTheme";
import { getHoldingsRequest } from "../../redux/Dashboard/dashboardSlice";


const findHolding = (holdings, bullion) =>
  (holdings || []).find(
    (h) =>
      h.metalName === bullion.metalName &&
      Number(h.purity) === Number(bullion.purity),
  );

/* ---------------- COLUMNS ---------------- */
const getColumns = (navigate, themeColors, holdings, decimal) => [
  {
    field: "name",
    headerName: "Plan Name",
    flex: 2,
  },
  {
    field: "duration",
    headerName: "Duration",
    flex: 1,
    render: (value, row) => `${value} ${row.unit.toLowerCase()}`,
  },
  {
    field: "qty",
    headerName: "Min Qty (gm)",
    flex: 1,
  },
  {
    field: "metal",
    headerName: "Metal",
    flex: 1,
    render: (value, row) =>
      `${row.bullion.metalName}-${row.bullion.karat}k-${row.bullion.purity}`,
  },
  {
    field: "benefit",
    headerName: "Benefit (gm)",
    flex: 1,
    render: (value, row) => {
      const benefit = Number(
        ((row.qty * row.interest) / 100 / 365) * row.duration,
      ).toFixed(4);
      return (
        <Typography variant="body2">
          {benefit} gm for every {row.qty} gm
        </Typography>
      );
    },
  },
  {
    field: "action",
    headerName: "Action",
    flex: 1,
    render: (value, row) => {
      const holding = findHolding(holdings, row.bullion);
      return (
        <Button
          variant="contained"
          size="small"
          sx={{
            background: themeColors.mediumContainer,
            color: themeColors.text,
            border: `1px solid ${themeColors.scaffold}`,
            textTransform: "none",
            fontWeight: 600,
            "&:hover": { background: themeColors.svgColor },
          }}
          onClick={() =>
            navigate("/depositPlan", {
              state: {
                plan: row,
                balance: {
                  metalName: row.bullion.metalName,
                  purity: row.bullion.purity,
                  karat: row.bullion.karat,
                  availableBalance: Number(holding?.availableBalance) || 0,
                },
              },
            })
          }
        >
          View Details
        </Button>
      );
    },
  },
];

const AllDepositPlanScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const { depositPlans } = useSelector((state) => state.deposit);

  const { data: holdings = [] , appConfig} = useSelector((state) => state.dashboard || {});
   const decimal = appConfig?.decimalPrecision ?? 4;
  const themeColors = useAppTheme();

  useEffect(() => {
    setLoading(true);
    dispatch(getDepositPlansRequest());
  }, [dispatch]);

  useEffect(() => {
    setLoading(true);
    dispatch(getDepositPlansRequest());
    dispatch(
      getHoldingsRequest({ jeweller: `${process.env.REACT_APP_JEWELLER_ID}` }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(depositPlans)) {
      setData(
        depositPlans.map((plan) => ({
          ...plan,
          id: plan._id,
        })),
      );
      setLoading(false);
    }
  }, [depositPlans]);

  const handleTableChange = useCallback((type, payload) => {
    if (type === "changePage") {
      setPage(payload.page);
      setRowsPerPage(payload.rowsPerPage);
    } else if (type === "changeRowsPerPage") {
      setPage(payload.page);
      setRowsPerPage(payload.rowsPerPage);
    }
  }, []);
  //
  const paginatedData = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Box
      sx={{ width: "100%", display: "flex", justifyContent: "center", mt: 8 }}
    >
      <Card
        sx={{
          width: "98%",
          maxWidth: "1300px",
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
          borderRadius: 3,
          background: themeColors.scaffoldGradientColors,
        }}
      >
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: themeColors.primary,
                fontFamily: "Aeonik",
              }}
            >
              Bullion Deposit Plans
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => navigate("/depositPlanHistory")}
              sx={{
                background: themeColors.mediumContainer,
                color: themeColors.text,
                border: `1px solid ${themeColors.scaffold}`,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "15px",
                px: 3,
                "&:hover": { background: themeColors.svgColor },
              }}
            >
              Deposit History
            </Button>
          </Stack>

          {loading ? (
            <Box sx={{ textAlign: "center", mt: 10 }}>
              <CircularProgress color="primary" />
              <Typography variant="body2" mt={2}>
                Loading deposit plans...
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                height: "70vh",
                width: "100%",
                minWidth: 0,
                overflow: "auto",
              }}
            >
              <TableCompo
                title="Bullion Deposit Plans"
                columns={getColumns(navigate, themeColors, holdings, decimal)}
                data={paginatedData}
                page={page}
                limit={rowsPerPage}
                totalCount={data.length}
                onTableChange={handleTableChange}
                loading={loading}
                ref={null}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AllDepositPlanScreen;
